import { useState, useEffect, useCallback } from 'react';
import { axiosGet } from '../../../../../utils/axiosGet';
import { devError } from '../../../../../utils/devLog';

const useChairReportData = ({
  committeeId,
  candidates,
  committeeMembers,
  chairName,
  formatCandidate,
  electionTypeId,
}) => {
  const [formData, setFormData] = useState({
    gapAnalysis: '',
    evaluationProcess: '',
    integrityMatters: '',
    diversityIssues: '',
    fnsFeedback: '',
    generalObservations: '',
    memberAttendance: [],
    candidateReports: [],
  });

  const [reportId, setReportId] = useState(null);
  const [reportStatus, setReportStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [deadline, setDeadline] = useState(null);

  const initialiseMemberAttendance = useCallback(
    (existingAttendance = []) => {
      const allMembers = [];

      if (chairName) {
        const existingChair = existingAttendance.find(
          (a) => a.role === 'Chair',
        );
        allMembers.push({
          userId: 'chair',
          memberName: chairName,
          role: 'Chair',
          attendance: existingChair?.attendance || '',
        });
      }

      if (committeeMembers && committeeMembers.length > 0) {
        committeeMembers.forEach((member) => {
          const existing = existingAttendance.find(
            (a) => a.userId === member.id,
          );
          allMembers.push({
            userId: member.id,
            memberName: member.name,
            role: member.role || 'Member',
            attendance: existing?.attendance || '',
          });
        });
      }

      return allMembers;
    },
    [committeeMembers, chairName],
  );

  const initialiseCandidateReports = useCallback(
    (existingReports = []) => {
      if (!candidates || candidates.length === 0) return [];

      return candidates.map((candidate) => {
        const f = formatCandidate(candidate, electionTypeId);

        const existing = existingReports.find(
          (r) =>
            (r.candidateId != null &&
              candidate.candidateId != null &&
              Number(r.candidateId) === Number(candidate.candidateId)) ||
            (r.candidateId == null &&
              Number(r.candidateRank) === Number(f.rank)),
        );

        return {
          candidateId: candidate.candidateId,
          candidateName: f.name,
          candidateRank: f.rank,
          report: existing?.report || '',
          fieldOfExpertise: existing?.fieldOfExpertise || '',
        };
      });
    },
    [candidates, formatCandidate, electionTypeId],
  );

  const fetchReport = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);

        try {
          const deadlineResponse = await axiosGet('chair-report-deadline');
          if (deadlineResponse?.data?.data?.attributes?.deadline) {
            setDeadline(deadlineResponse.data.data.attributes.deadline);
          }
        } catch (e) {
          devError('Error fetching deadline:', e);
        }

        try {
          const reportResponse = await axiosGet(
            `chair-reports?filters[sectionalCommitteeId][$eq]=${committeeId}`,
          );

          const existingReport = reportResponse?.data?.data?.[0];

          if (existingReport) {
            setReportId(existingReport.id);
            const attrs = existingReport.attributes;
            setReportStatus(attrs.status || 'draft');

            setFormData({
              gapAnalysis: attrs.gapAnalysis || '',
              evaluationProcess: attrs.evaluationProcess || '',
              integrityMatters: attrs.integrityMatters || '',
              diversityIssues: attrs.diversityIssues || '',
              fnsFeedback: attrs.fnsFeedback || '',
              generalObservations: attrs.generalObservations || '',
              memberAttendance: initialiseMemberAttendance(
                attrs.memberAttendance || [],
              ),
              candidateReports: initialiseCandidateReports(
                attrs.candidateReports || [],
              ),
            });
          } else {
            setFormData((prev) => ({
              ...prev,
              memberAttendance: initialiseMemberAttendance(),
              candidateReports: initialiseCandidateReports(),
            }));
          }
        } catch (e) {
          setFormData((prev) => ({
            ...prev,
            memberAttendance: initialiseMemberAttendance(),
            candidateReports: initialiseCandidateReports(),
          }));
        }
      } catch (err) {
        devError('Error fetching chair report:', err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [committeeId, initialiseMemberAttendance, initialiseCandidateReports],
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    formData,
    setFormData,
    reportId,
    setReportId,
    reportStatus,
    setReportStatus,
    loading,
    deadline,
    refetchReport: fetchReport,
  };
};

export default useChairReportData;
